/**
 * The ladder for one underlying — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EXTRACTED SO THERE IS ONE OF IT, NOT TO MAKE `Terminal` SHORTER
 *
 * Everything here used to live inline in `Terminal.tsx`, which was fine while
 * the Terminal was the only screen with a chain on it. The basket builder is
 * the second, and a second copy of this is not a bigger file — it is two
 * ladders that can disagree about where the money is.
 *
 * What is subtle enough to be worth saying: the window is ANCHORED (it does
 * not re-centre as spot moves, because every row would shift under a click),
 * the at-the-money strike comes from the FORWARD rather than the spot, and
 * the greeks are solved once per pass and read by the margin scan. Each of
 * those is a decision with a comment explaining it, and each would have to be
 * rediscovered — or quietly got wrong — in a copy.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT STAYS WITH THE CALLER
 *
 * Rendering, and anything that depends on how much of the window the chain
 * has: which columns are drawn, whether the open-interest bar is shown, what
 * a click on B or S does. This hook answers "what IS the ladder" and not
 * "what does this screen do with it" — which is the same line `chainWindow.ts`
 * already draws between the catalogue and the view.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { INDEX_SPECS, istDateOf, kindsOfExpiry, resolveAtm, type ExpiryKind } from '@apna/shared'
import { SPOT_TOKENS } from '@/feed/spotTokens'
import { useForegroundChain, useTickCell, useTickSubscription } from '@/feed/useTick'
import { useSnapshot } from '@/feed/useSnapshot'
import { chainWindow, nearestStrikeIndex, widenForDrift, widenForStrategy } from '../chainWindow'
import { useChainCatalogue, type ChainLadderRow } from './useChainCatalogue'
import { useChainGreeks, type ChainGreeks } from './useChainGreeks'
import type { PreviewChainRow } from '@/hooks/useStrategyPreviews'

/**
 * Strikes either side of ATM shown by default.
 *
 * The default thirty-three rows fit within the forty rows warmed per expiry,
 * leaving coverage beyond the initial viewport for small ATM moves.
 */
export const LIVE_HALF_WIDTH = 16

/** How much further one "load more" reaches. */
export const LOAD_MORE_STRIKES = 10

export interface ChainLadder {
  /** Every strike this expiry lists. The resolver's input — see 9.40. */
  rows: readonly ChainLadderRow[]
  /** The windowed slice the table draws. */
  visible: readonly ChainLadderRow[]
  hiddenAbove: number
  hiddenBelow: number
  showMoreAbove: () => void
  showMoreBelow: () => void

  expiries: readonly string[]
  expiry: string | null
  setExpiry: (iso: string) => void
  loading: boolean
  error: string | null

  spot: number
  step: number
  /** From the FORWARD when there is one — see below. Null before the first tick. */
  atm: number | null
  /** Carries `refresh`, which asks for an immediate solve. */
  greeks: ChainGreeks & { readonly refresh: () => void }
  lotSize: number | null
  isExpiryDay: boolean
  chainKinds: readonly ExpiryKind[]
  /** The chain as `useStrategyPreviews` and the resolver need it. */
  previewRows: readonly PreviewChainRow[]
  /** Every token on screen, subscribed and in the foreground. */
  chainTokens: readonly number[]
  /** The scroll port, for the caller's `<tbody>`. */
  bodyRef: React.RefObject<HTMLDivElement | null>
}

export interface ChainLadderOptions {
  /**
   * ATM offsets some armed strategy needs on screen — 9.21.
   *
   * Optional because the basket builder has no armed strategy: its legs are
   * contracts the trader clicked, so every one of them was already visible.
   */
  strategySteps?: readonly number[]
}

export function useChainLadder(
  underlying: string,
  { strategySteps = EMPTY_STEPS }: ChainLadderOptions = {},
): ChainLadder {
  const { rows, expiries, expiry, setExpiry, loading, error } = useChainCatalogue(underlying)

  const spotToken = SPOT_TOKENS[underlying] ?? 0
  const { value: spot } = useTickCell(spotToken, 'ltp')
  const step = INDEX_SPECS[underlying]?.step ?? 50

  /*
   * THE SEED, not the answer.
   *
   * Picking the at-the-money strike needs a forward, and recovering the
   * forward by put-call parity needs a strike to read the straddle at — so
   * it bootstraps: the spot chooses a seed, the seed's straddle gives the
   * forward, and the forward chooses the strike this chain actually marks.
   *
   * The seed stays SPOT-based deliberately. Deriving it from the forward
   * would be a cycle, and one that could oscillate between two adjacent
   * strikes every time the basis crossed half a step.
   */
  const atmSeed = spot > 0 ? resolveAtm(spot, step) : null

  /*
   * THE LADDER IS WINDOWED AROUND ATM.
   *
   * NIFTY lists 236 strikes while the spot is near the middle of them, so the
   * full ladder is a hundred rows of dashes in both directions around the only
   * part anyone reads — and §4.4 keeps ATM ±16 always on, so 236 strikes is
   * 472 subscriptions against a 460 ceiling for one index alone.
   */
  const [extraAbove, setExtraAbove] = useState(0)
  const [extraBelow, setExtraBelow] = useState(0)

  /**
   * WHERE THE LADDER IS PINNED — ROADMAP 4.19.
   *
   * Fixed at the first real ATM of this ladder and not moved again. A window
   * that re-centres drops a row off one end on every tick that crosses a
   * half-strike boundary, and every row on screen moves a row height — on a
   * screen where a click is the order.
   */
  const [anchor, setAnchor] = useState<number | null>(null)

  // The anchor is an INDEX into these rows, so it means nothing once they change.
  useEffect(() => {
    setAnchor(null)
    setExtraAbove(0)
    setExtraBelow(0)
  }, [underlying, expiry])

  const liveAtmIndex = useMemo(
    () =>
      nearestStrikeIndex(
        rows.map((r) => r.strike),
        atmSeed,
      ),
    [rows, atmSeed],
  )

  /*
   * A JOINED STRING, because `strategySteps` is a fresh array on most renders
   * and an effect keyed on the array itself would run on every one of them.
   */
  const stepsKey = strategySteps.join(',')

  /*
   * Pin on the first real ATM; afterwards, answer drift by WIDENING.
   *
   * Both in one effect because they are one rule — "the centre is set once" —
   * and splitting them would let a render land between the anchor being taken
   * and the drift being measured against it.
   */
  useEffect(() => {
    if (liveAtmIndex < 0) return
    if (anchor === null) {
      setAnchor(liveAtmIndex)
      return
    }
    const drifted = widenForDrift(anchor, liveAtmIndex, {
      above: extraAbove,
      below: extraBelow,
    })
    const want = widenForStrategy(
      drifted,
      stepsKey === '' ? [] : stepsKey.split(',').map(Number),
      LIVE_HALF_WIDTH,
    )
    // Compared by value: both helpers return a fresh object, and setting state
    // to an equal one on every tick would re-render the whole chain.
    if (want.above !== extraAbove) setExtraAbove(want.above)
    if (want.below !== extraBelow) setExtraBelow(want.below)
  }, [liveAtmIndex, anchor, extraAbove, extraBelow, stepsKey])

  const atmIndex =
    anchor ??
    (liveAtmIndex >= 0 ? liveAtmIndex : rows.length > 0 ? Math.floor(rows.length / 2) : -1)

  const { visible, hiddenAbove, hiddenBelow } = useMemo(
    () => chainWindow(rows, atmIndex, LIVE_HALF_WIDTH, extraAbove, extraBelow),
    [rows, atmIndex, extraAbove, extraBelow],
  )

  /*
   * ROWS ADDED ABOVE MUST NOT PUSH THE VIEW DOWN.
   *
   * Widening at LOWER strikes prepends rows, and a scroll container keeps its
   * `scrollTop` — so everything the trader is looking at slides down by
   * exactly the height that was inserted. Measured rather than computed from
   * a row height: the row is 46px today and a stylesheet is not a contract.
   */
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const lastHeight = useRef(0)
  const lastExtraAbove = useRef(0)
  useLayoutEffect(() => {
    const el = bodyRef.current
    if (!el) return
    if (extraAbove > lastExtraAbove.current && lastHeight.current > 0) {
      el.scrollTop += el.scrollHeight - lastHeight.current
    }
    lastExtraAbove.current = extraAbove
    lastHeight.current = el.scrollHeight
  }, [extraAbove, visible.length])

  /*
   * ROADMAP 6.6. IV for the VISIBLE window only — a hidden strike's IV is work
   * nobody is reading, and the window is already what the engine subscribes.
   */
  const greeksRows = useMemo(
    () =>
      visible.map((r) => ({
        strike: r.strike,
        ceToken: r.ce?.brokerToken ? Number(r.ce.brokerToken) : null,
        peToken: r.pe?.brokerToken ? Number(r.pe.brokerToken) : null,
      })),
    [visible],
  )
  const greeks = useChainGreeks(greeksRows, { expiry, atmStrike: atmSeed })

  /**
   * The strike this chain marks AT THE MONEY, from the FORWARD not the spot.
   *
   * The money is where the forward is, and the same parity forward prices
   * every IV and delta in the ladder — so marking ATM off the spot made the
   * badge and the greeks disagree about where the money was.
   */
  const atm = greeks.forward !== null ? resolveAtm(greeks.forward, step) : atmSeed

  const chainTokens = useMemo(
    () =>
      visible.flatMap((r) =>
        [r.ce?.brokerToken, r.pe?.brokerToken]
          .filter((t): t is string => Boolean(t))
          .map(Number)
          .filter((t) => Number.isInteger(t) && t > 0),
      ),
    [visible],
  )

  /*
   * Does this expiry settle TODAY? The exchange adds a further 2% ELM to
   * short index options on their expiry day, hedged or not, so the margin
   * estimate has to know.
   */
  const isExpiryDay = useMemo(
    () => expiry !== null && expiry === istDateOf(Date.now()),
    [expiry],
  )

  /*
   * WHICH KINDS THIS CHAIN ANSWERS TO — 9.42. A stored leg carries a KIND
   * ("this week") and the chain on screen is a DATE; this is the translation.
   */
  const chainKinds = useMemo(
    () => (expiry === null ? [] : kindsOfExpiry(expiry, expiries)),
    [expiry, expiries],
  )

  /*
   * RESOLVING IS NOT RENDERING — 9.40. Every strike of the expiry, not the
   * window: whether a leg can become an instrument is a question for the
   * CATALOGUE, and the window exists to decide what a human reads.
   */
  const previewRows = useMemo(
    () =>
      rows.map((r) => ({
        strike: r.strike,
        ceToken: r.ce?.brokerToken ? Number(r.ce.brokerToken) : null,
        peToken: r.pe?.brokerToken ? Number(r.pe.brokerToken) : null,
      })),
    [rows],
  )

  useTickSubscription(chainTokens)
  /*
   * ROADMAP 3.19. The same window, declared as the chain being READ, which is
   * what buys open interest — the expensive half, a second subscription per
   * contract at the broker, with room for about one chain's worth.
   */
  useForegroundChain(chainTokens)
  // In PARALLEL with the subscribe, not before it (§4.8a).
  useSnapshot(chainTokens)

  /*
   * CENTRE ON THE MONEY, ONCE PER CHAIN — not on every ATM move. Re-centring
   * under someone who is reading a far strike is worse than being slightly
   * off, and the anchor exists for the same reason.
   */
  const scrolledFor = useRef<string | null>(null)
  useEffect(() => {
    if (atm === null || visible.length === 0) return
    const key = `${underlying}|${expiry ?? ''}`
    if (scrolledFor.current === key) return
    const row = bodyRef.current?.querySelector(`[data-testid="chain-row-${atm}"]`)
    if (!row) return
    scrolledFor.current = key
    row.scrollIntoView({ block: 'center' })
  }, [atm, visible.length, underlying, expiry])

  const showMoreAbove = useCallback(() => setExtraAbove((n) => n + LOAD_MORE_STRIKES), [])
  const showMoreBelow = useCallback(() => setExtraBelow((n) => n + LOAD_MORE_STRIKES), [])

  const staticSpec = INDEX_SPECS[underlying]
  /*
   * Contract specs come from the CHAIN, not the static table. `INDEX_SPECS` is
   * a bootstrap fallback and it disagrees: it reports NIFTY's lot size as 75
   * where the live master says 65 — and a lot size is what an order quantity
   * is computed from, so the wrong one is a wrong order, not a wrong label.
   */
  const contractSpec = rows.length > 0 ? (rows[0]?.ce ?? rows[0]?.pe) : null
  const lotSize = contractSpec?.lotSize ?? staticSpec?.lot ?? null

  return {
    rows,
    visible,
    hiddenAbove,
    hiddenBelow,
    showMoreAbove,
    showMoreBelow,
    expiries,
    expiry,
    setExpiry,
    loading,
    error,
    spot,
    step,
    atm,
    greeks,
    lotSize,
    isExpiryDay,
    chainKinds,
    previewRows,
    chainTokens,
    bodyRef,
  }
}

/** Stable, so the default argument does not re-key the widening effect. */
const EMPTY_STEPS: readonly number[] = []
